import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Checkbox, Input, Radio, Spin } from 'antd';
import { CheckboxValueType } from 'antd/es/checkbox/Group';
import cn from 'classnames';
import _ from 'lodash';

import { deepClone } from '../../../../../../../helpers/utils/deepClone';
import {
  searchFilter,
  updateFilterItemInList,
} from '../../../../../../../helpers/utils/reports';
import { unApi } from '../../../../../api/endpoints/reports/unApi';
import { useActions } from '../../../../../hooks/useActions';
import { useTypedSelector } from '../../../../../hooks/useTypedSelector';
import ListTextSelector from '../ListTextSelector';

/** Интерфейс элемента списка */
interface ListItem {
  id: number | string;
  name: string;
}

/** Интерфейс данных фильтра */
interface FilterData {
  items: ListItem[];
  checkeds: ListItem[];
  [key: string]: unknown;
}

/** Props для ListPicker */
interface ListPickerProps {
  isModal?: boolean;
  isLocalMode?: boolean;
  localFilters?: any[];
  setLocalFilters?: (filters: any[]) => void;
  disabled?: boolean;
  id: string;
  index?: number;
  modalVisibleHandler: (visible: boolean) => void;
  itemData?: FilterData;
  list?: boolean;
  items?: ListItem[];
  [key: string]: unknown;
}

const ListPicker: React.FC<ListPickerProps> = ({
  isModal = false,
  isLocalMode = false,
  localFilters,
  setLocalFilters,
  disabled = false,
  id,
  index,
  modalVisibleHandler,
  itemData,
  list,
  items: propsItems,
  ...props
}) => {
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const { filters: globalFilters } = useTypedSelector((state) => 
    isModal ? state.filter.dashboardModal : state.filter
  );
  const filters = isLocalMode ? localFilters || [] : globalFilters;
  const { setFilters, setFilter, setMetaFilterUpdate } = useActions();

  const [filterMode, setFilterMode] = useState(1);
  const [searchValue, setSearchValue] = useState('');

  // Приводим id к строке для безопасной работы с методами строк
  const safeId = String(id ?? '');

  // Вычисляем ID с учётом режима include/exclude
  const filterId = useMemo(() => {
    if (filterMode === 2) {
      return safeId.includes('not_') ? safeId : `not_${safeId}`;
    }
    return safeId.includes('not_') ? safeId.split('not_')[1] : safeId;
  }, [safeId, filterMode]);

  // Инициализация режима из id
  useEffect(() => {
    setFilterMode(safeId.includes('not_') ? 2 : 1);
  }, [safeId]);

  // Определяем режим редактирования
  const isEditing = useMemo(() => {
    return Boolean(list && itemData);
  }, [list, itemData]);

  // Мемоизированные начальные данные фильтра
  const initialFilter = useMemo((): FilterData => {
    if (itemData && list) {
      return itemData;
    }
    return { items: [], checkeds: [], ...props };
  }, [itemData, list, props]);

  const [items, setItems] = useState<ListItem[]>(propsItems || initialFilter.items);
  const [currentData, setCurrentData] = useState<FilterData>(initialFilter);

  // Синхронизация currentData при изменении itemData
  useEffect(() => {
    if (itemData && list) {
      setCurrentData(itemData);
      if (itemData.items?.length) {
        setItems(itemData.items);
      }
    }
  }, [itemData, list]);

  const canApply = isEditing || currentData.checkeds.length > 0;

  const onApply = useCallback(() => {
    // Запрещаем добавление нового фильтра без выбранных значений
    if (!canApply) {
      return;
    }

    const _currentData = {
      ...currentData,
      id: filterId,
    };

    if (isLocalMode && setLocalFilters) {
      // Локальный режим - используем callback
      if (isEditing && index !== undefined) {
        let _filters = _.cloneDeep(filters);
        _filters = updateFilterItemInList(_filters, index, _currentData);
        setLocalFilters(_filters);
      } else {
        setLocalFilters([...filters, _currentData]);
      }
    } else {
      // Глобальный режим - используем Redux
      if (isEditing && index !== undefined) {
        let _filters = _.cloneDeep(filters);
        _filters = updateFilterItemInList(_filters, index, _currentData);
        setFilters(isModal ? { isModal, data: _filters } : _filters);
      } else {
        setFilter({ ..._currentData, isModal });
      }
      setMetaFilterUpdate({ value: true });
    }

    modalVisibleHandler(false);
  }, [canApply, currentData, filterId, isEditing, index, filters, isLocalMode, setLocalFilters, isModal, setFilters, setFilter, setMetaFilterUpdate, modalVisibleHandler]);

  // Загрузка элементов списка
  useEffect(() => {
    if ((items && items.length !== 0) || !selectedSchool || propsItems) return;
    
    unApi
      .getFilterList(parseInt(selectedSchool?.id || '0'), id)
      .then((response) => {
        let filtersData = deepClone(response.data) as ListItem[];
        if (location.pathname.includes('/source/ads')) {
          filtersData = filtersData.filter(item => item.id !== 8);
        }
        setItems(filtersData);
      })
      .catch(() => {
        // Ошибка загрузки списка
      });
  }, [id, items?.length, selectedSchool, propsItems]);

  const setCheckeds = useCallback((checkeds: ListItem[]) => {
    setCurrentData((prevState) => ({
      ...prevState,
      items,
      checkeds,
    }));
  }, [items]);

  const onChangeCheck = useCallback((checkedValue: CheckboxValueType[]) => {
    const checkeds = items.filter(({ id }) => checkedValue.includes(id));
    setCurrentData((prevState) => ({
      ...prevState,
      items,
      checkeds,
    }));
  }, [items]);

  const onChangeSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const checkAll = useCallback(() => {
    setCurrentData((prevState) => ({
      ...prevState,
      checkeds: items.filter((item) => searchFilter(item, searchValue)),
    }));
  }, [items, searchValue]);

  const onClear = useCallback(() => {
    setCurrentData((prevState) => ({
      ...prevState,
      checkeds: [],
    }));
  }, []);

  // Мемоизированные значения для Checkbox.Group
  const checkboxGroupValue = useMemo(() => 
    currentData.checkeds.map((item) => item.id),
    [currentData.checkeds]
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !disabled && canApply) {
      e.preventDefault();
      onApply();
    }
  }, [disabled, canApply, onApply]);

  return (
    <div className="picker-container" onKeyDown={handleKeyDown}>
      <Input
        disabled={disabled}
        onChange={onChangeSearch}
        placeholder="Найти"
        allowClear
      />

      <div className="picker-radio-group">
        <Radio.Group
          disabled={disabled}
          onChange={(e) => setFilterMode(e.target.value)}
          value={filterMode}
        >
          <Radio value={1}>Включить</Radio>
          <Radio value={2}>Исключить</Radio>
        </Radio.Group>
      </div>

      <div className="picker-buttons">
        <Button disabled={disabled} onClick={checkAll} size="small">
          Выбрать все
        </Button>

        <ListTextSelector id={id as unknown as number} list={items as Array<{id: number; name: string}>} setList={setCheckeds} />

        <Button disabled={disabled} onClick={onClear} size="small">
          Очистить
        </Button>
      </div>

      <div className="picker-list">
        {items?.length === 0 && (
          <div className="load-spin">
            <Spin spinning={true} tip="Загрузка..." />
          </div>
        )}

        <Checkbox.Group
          disabled={disabled}
          value={checkboxGroupValue}
          onChange={onChangeCheck}
        >
          {items?.map((item) => {
            const hide = !searchFilter(item, searchValue);
            return (
              <div
                key={item.id}
                className={cn('listpicker-item', {
                  'listpicker-item-hide': hide,
                })}
              >
                <Checkbox value={item.id} style={{ marginRight: 7 }}>
                  {item.name && item.name.replace(/&nbsp;/g, '\u00A0')}
                </Checkbox>
              </div>
            );
          })}
        </Checkbox.Group>
      </div>

      <Button
        disabled={disabled || !canApply}
        type="primary"
        className="picker-apply-btn"
        onClick={onApply}
      >
        Применить
      </Button>
    </div>
  );
};

export default ListPicker;
